/**
 * Research page - company fundamentals, SEC filings, insider/institutional activity
 *
 * URL patterns:
 * - /research - No symbol selected, shows symbol selector
 * - /research/:symbol - Shows research data (default: overview tab)
 * - /research/:symbol/:tab - Shows specific tab for symbol
 */
import { useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useHoldings, useWatchlist } from '../hooks'
import { useCompanyMetrics, useResearchableSymbols } from '../hooks/useResearch'
import { LoadingCard } from '../components/common'
import { OverviewTab } from '../components/research/OverviewTab'
import { FilingsTab } from '../components/research/FilingsTab'
import { InsiderTab } from '../components/research/InsiderTab'
import { InstitutionalTab } from '../components/research/InstitutionalTab'
import { AITab } from '../components/research/AITab'
import { NewsTab } from '../components/research/NewsTab'
import { EarningsTab } from '../components/research/EarningsTab'

// Valid tabs - URL parameter is validated against this list
const VALID_TABS = ['overview', 'sec-filings', 'insider', 'institutional', 'earnings', 'news', 'ai'] as const
type ResearchTab = typeof VALID_TABS[number]

// Format helpers
function formatMarketCap(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`
  return `$${value.toLocaleString()}`
}

function formatRatio(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return value.toFixed(2)
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  // Value is already a decimal (e.g., 0.55 = 55%)
  return `${(value * 100).toFixed(1)}%`
}

// Summary card component
function SummaryCard({
  label,
  value,
  subValue,
}: {
  label: string
  value: string
  subValue?: string
}) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
      {subValue && (
        <div className="mt-1 text-sm text-gray-500">{subValue}</div>
      )}
    </div>
  )
}


export function ResearchPage() {
  const { symbol: encodedSymbol, tab: urlTab } = useParams<{ symbol?: string; tab?: string }>()
  const navigate = useNavigate()

  // Decode symbol from URL (e.g., "BTC%2FUSD" -> "BTC/USD")
  const urlSymbol = encodedSymbol ? decodeURIComponent(encodedSymbol) : undefined

  // Derive active tab from URL, default to 'overview' for invalid/missing tabs
  const activeTab: ResearchTab = urlTab && VALID_TABS.includes(urlTab as ResearchTab)
    ? (urlTab as ResearchTab)
    : 'overview'

  // Get holdings, watchlist, and researchable symbols for dropdown
  const { data: holdings, isLoading: holdingsLoading } = useHoldings()
  const { data: watchlist, isLoading: watchlistLoading } = useWatchlist()
  const { data: researchableSymbols, isLoading: researchableLoading } = useResearchableSymbols()

  // Get company metrics for summary cards
  const { data: metrics, isLoading: metricsLoading } = useCompanyMetrics(urlSymbol)

  // Build unique symbols list from holdings + watchlist + researchable symbols
  // This ensures macro indicators like UUP are accessible via alert navigation
  const availableSymbols = useMemo(() => {
    const symbolSet = new Set<string>()

    // Add holding symbols
    holdings?.forEach((h) => symbolSet.add(h.symbol))

    // Add watchlist symbols
    watchlist?.forEach((w) => symbolSet.add(w.symbol))

    // Add researchable symbols (fundamentals, prices, alerts)
    researchableSymbols?.forEach((s) => symbolSet.add(s))

    return Array.from(symbolSet).sort()
  }, [holdings, watchlist, researchableSymbols])

  const isLoadingSymbols = holdingsLoading || watchlistLoading || researchableLoading

  // Handle symbol change from dropdown - preserves current tab
  const handleSymbolChange = (newSymbol: string) => {
    if (newSymbol) {
      // Encode symbol for URL (e.g., "BTC/USD" -> "BTC%2FUSD")
      navigate(`/research/${encodeURIComponent(newSymbol)}/${activeTab}`)
    } else {
      navigate('/research')
    }
  }

  // Handle tab change - updates URL
  const handleTabChange = (tab: ResearchTab) => {
    if (urlSymbol) {
      navigate(`/research/${encodeURIComponent(urlSymbol)}/${tab}`)
    }
  }

  // Tab definitions
  const tabs: { id: ResearchTab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'sec-filings', label: 'SEC Filings' },
    { id: 'insider', label: 'Insider Activity' },
    { id: 'institutional', label: 'Institutional' },
    { id: 'earnings', label: 'Earnings' },
    { id: 'news', label: 'News' },
    { id: 'ai', label: 'AI' },
  ]

  // Render tab content
  const renderTabContent = () => {
    if (!urlSymbol) {
      return (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">
            Select a symbol above to view research data.
          </p>
        </div>
      )
    }

    if (metricsLoading) {
      return <LoadingCard />
    }

    switch (activeTab) {
      case 'overview':
        return <OverviewTab symbol={urlSymbol} />
      case 'sec-filings':
        return <FilingsTab symbol={urlSymbol} />
      case 'insider':
        return <InsiderTab symbol={urlSymbol} />
      case 'institutional':
        return <InstitutionalTab symbol={urlSymbol} />
      case 'earnings':
        return <EarningsTab symbol={urlSymbol} />
      case 'news':
        return <NewsTab symbol={urlSymbol} />
      case 'ai':
        return <AITab symbol={urlSymbol} />
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      {/* Header with Symbol Selector */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Research</h1>
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">Symbol:</label>
          {isLoadingSymbols ? (
            <div className="w-40 h-10 bg-gray-100 rounded-md animate-pulse" />
          ) : (
            <select
              value={urlSymbol || ''}
              onChange={(e) => handleSymbolChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-w-[160px]"
            >
              <option value="">Select symbol...</option>
              {availableSymbols.map((symbol) => (
                <option key={symbol} value={symbol}>
                  {symbol}
                </option>
              ))}
            </select>
          )}
          {/* Contextual What-If actions */}
          {urlSymbol && (
            <div className="flex items-center space-x-2 border-l border-gray-200 pl-4">
              <Link
                to={`/what-if/buy?symbol=${encodeURIComponent(urlSymbol)}`}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Simulate Buy
              </Link>
              <Link
                to={`/what-if/position?symbol=${encodeURIComponent(urlSymbol)}`}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-purple-700 bg-purple-50 rounded-md hover:bg-purple-100 transition-colors"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Position Size
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards Row */}
      {urlSymbol && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            label="Market Cap"
            value={formatMarketCap(metrics?.market_cap)}
          />
          <SummaryCard
            label="P/E Ratio"
            value={formatRatio(metrics?.price_to_earnings)}
            subValue={metrics?.peg_ratio ? `PEG: ${formatRatio(metrics.peg_ratio)}` : undefined}
          />
          <SummaryCard
            label="Revenue Growth"
            value={formatPercent(metrics?.revenue_growth)}
            subValue={metrics?.earnings_growth ? `Earnings: ${formatPercent(metrics.earnings_growth)}` : undefined}
          />
          <SummaryCard
            label="Net Margin"
            value={formatPercent(metrics?.net_margin)}
            subValue={metrics?.return_on_equity ? `ROE: ${formatPercent(metrics.return_on_equity)}` : undefined}
          />
        </div>
      )}

      {/* Tab Navigation */}
      {urlSymbol && (
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-4 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  )
}

export default ResearchPage
